// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE SESSION ROLL — what happens when a conversation reaches its ceiling.
//
// Erez asked for this on 14 Aug 2026, after the first live end-to-end test
// bricked his own conversation: "there's still a small issue here because if it
// continues to talk to a session that finished, or to a session that is
// actually still in progress because it's saving the session summary, that's a
// problem." He is right, and neither diagram below is decoration — the second
// one only makes sense as a set of edits to the first.
//
// These are DATA FLOW diagrams (skill://diagrams, archetype 3): the question is
// "what happens to one message, in order?", so the numbered discs ARE the
// diagram. Stages stay TOP-LEVEL columns rather than nesting inside one band,
// because c4 paints the wire SVG first and the boxes over it — a wire routed
// inside a container is painted out (Atlas, 6 Aug: the TWO LOOPS diagram had
// unreadable numbers for three days because every review read the source).
//
// THE ACCENT IS SPENT ONCE, ON THE FAILURE. In diagram 1 it marks the backstop
// that denies the turn and the two windows where Dan is talking to the wrong
// session. In diagram 2 those boxes are gone rather than fixed — every change
// is a deletion or a reordering, which is the point.
//
// ⚠️ DRAWN FROM THE CODE, NOT A PLAN. Diagram 1 is falcon_policies.context_ceiling
// and falcon_app.session.roll as they exist at commit 4bd6298, including the
// phase each step runs in — that phase split is the whole bug. Diagram 2 is a
// proposal and has not shipped.
//
// Owner agent: Otto. Falcon board only.
// ─────────────────────────────────────────────────────────────────────────────

import { Canvas, box, leaf, person, type Diagram } from "../command/c4";

// ═══ 1 · TODAY ═══════════════════════════════════════════════════════════════
// Two independent defects. The first is structural and permanent; the second is
// a race that costs either memory or continuity depending on when Dan types.

const NOW: Diagram = (() => ({
  key: "falcon-roll-now",
  title: "The roll as it runs today",
  subtitle: "falcon_policies.context_ceiling + session.roll · commit 4bd6298 · phases: request / llm_response / tool_call",
  cols: [
    {
      root: box("n-dan", undefined, "col", [person("n-p", "Dan")], { depth: 0 }),
      align: "center",
    },
    {
      root: box("n-req", "PHASE · request", "col", [
        leaf("n-in", "Message arrives", "[telegram]", "MessageSquare"),
        leaf("n-hard", "Past hard stop?", "[ceiling × 1.5]", "OctagonX", "gate"),
        leaf("n-deny", "DENY the turn", "[never runs]", "Ban", "gate"),
      ], { depth: 0 }),
    },
    {
      root: box("n-turn", "PHASE · llm_response", "col", [
        leaf("n-read", "Read context", "[usage tokens]", "Gauge"),
        leaf("n-cross", "Crossed ceiling", "[trigger]", "Flag"),
        leaf("n-hand", "Inject handover", "[hidden msg]", "Inbox"),
      ], { depth: 0 }),
    },
    {
      root: box("n-agent", "THE AGENT", "col", [
        leaf("n-ans", "Answers Dan", "[adds property]", "Check"),
        leaf("n-brief", "write_brief", "[3 objects]", "Save"),
        leaf("n-say", "“one moment”", "[final text]", "MessageCircle"),
      ], { depth: 0 }),
    },
    {
      root: box("n-roll", "session.roll", "col", [
        leaf("n-new", "New session", "[+ runner]", "Plus"),
        leaf("n-bind", "Bind pointer", "[SYNC]", "Link", "gate"),
        leaf("n-seed", "Seed rules+history", "[BACKGROUND]", "Hourglass", "gate"),
      ], { depth: 0 }),
    },
    {
      root: box("n-out", "WHAT DAN GETS", "col", [
        leaf("n-cold", "Cold agent", "[no rules, no memory]", "Snowflake", "gate"),
        leaf("n-lost", "Or content lost", "[written after brief]", "TriangleAlert", "gate"),
      ], { depth: 0 }),
    },
  ],
  wires: [
    { a: "n-p", b: "n-in", as: "r", bs: "l", label: "telegram", num: 1 },
    { a: "n-in", b: "n-hard", as: "b", bs: "t", num: 2 },
    { a: "n-hard", b: "n-deny", as: "b", bs: "t", label: "if over", red: true, num: 3 },
    { a: "n-hard", b: "n-read", as: "r", bs: "l", label: "if under", num: 4 },
    { a: "n-read", b: "n-cross", as: "b", bs: "t", num: 5 },
    { a: "n-cross", b: "n-hand", as: "b", bs: "t", num: 6 },
    { a: "n-hand", b: "n-ans", as: "r", bs: "l", label: "into the live turn", num: 7 },
    { a: "n-ans", b: "n-brief", as: "b", bs: "t", num: 8 },
    { a: "n-brief", b: "n-new", as: "r", bs: "l", label: "SAVED", num: 9 },
    { a: "n-new", b: "n-bind", as: "b", bs: "t", num: 10 },
    { a: "n-bind", b: "n-seed", as: "b", bs: "t", label: "returns first", red: true, num: 11 },
    { a: "n-seed", b: "n-cold", as: "r", bs: "l", label: "gap", red: true, dashed: true },
    { a: "n-say", b: "n-lost", as: "r", bs: "l", label: "gap", red: true, dashed: true },
    // THE DEADLOCK — the cure lives in a phase the guard prevents from running.
    { a: "n-deny", b: "n-roll", as: "b", bs: "b", label: "roll can never run", red: true, dashed: true },
  ],
  legend: [
    { style: "solid", label: "one message, in order" },
    { style: "red", label: "the two defects: a guard that blocks its own cure, and an unguarded gap" },
    { style: "dash", label: "what should happen and cannot" },
  ],
  padBottom: 96,
}))();

// ═══ 2 · PROPOSED ════════════════════════════════════════════════════════════
// Same spine. Three edits, every one a deletion or a reordering. The whole
// switch now completes INSIDE the turn Dan is still waiting on — which is why
// no queue, no hold and no "I'm ready" message are needed.

const FIXED: Diagram = (() => ({
  key: "falcon-roll-fixed",
  title: "The roll as it should run",
  subtitle: "Three edits, all subtractive · the switch finishes before Dan can read “one moment”",
  cols: [
    {
      root: box("f-dan", undefined, "col", [person("f-p", "Dan")], { depth: 0 }),
      align: "center",
    },
    {
      root: box("f-req", "PHASE · request", "col", [
        leaf("f-in", "Message arrives", "[telegram]", "MessageSquare"),
        leaf("f-allow", "ALWAYS allow", "[no backstop]", "Check"),
      ], { depth: 0 }),
    },
    {
      root: box("f-turn", "PHASE · llm_response", "col", [
        leaf("f-read", "Read context", "[usage tokens]", "Gauge"),
        leaf("f-cross", "Crossed ceiling", "[trigger]", "Flag"),
        leaf("f-hand", "Inject handover", "[hidden msg]", "Inbox"),
      ], { depth: 0 }),
    },
    {
      root: box("f-agent", "THE AGENT", "col", [
        leaf("f-ans", "Answers Dan", "[adds property]", "Check"),
        leaf("f-brief", "write_brief", "[3 objects]", "Save"),
        leaf("f-say", "“one moment”", "[final text]", "MessageCircle"),
      ], { depth: 0 }),
    },
    {
      root: box("f-roll", "session.roll · all synchronous", "col", [
        leaf("f-new", "New session", "[+ runner]", "Plus"),
        leaf("f-seed", "Seed rules+history", "[ENQUEUED FIRST]", "ListOrdered"),
        leaf("f-bind", "Bind pointer", "[LAST]", "Link"),
      ], { depth: 0 }),
    },
    {
      root: box("f-out", "WHAT DAN GETS", "col", [
        leaf("f-warm", "Warm agent", "[rules + history]", "Flame"),
        leaf("f-cont", "Nothing lost", "[queue is ordered]", "ShieldCheck"),
      ], { depth: 0 }),
    },
  ],
  wires: [
    { a: "f-p", b: "f-in", as: "r", bs: "l", label: "telegram", num: 1 },
    { a: "f-in", b: "f-allow", as: "b", bs: "t", num: 2 },
    { a: "f-allow", b: "f-read", as: "r", bs: "l", label: "never denied", num: 3 },
    { a: "f-read", b: "f-cross", as: "b", bs: "t", num: 4 },
    { a: "f-cross", b: "f-hand", as: "b", bs: "t", num: 5 },
    { a: "f-hand", b: "f-ans", as: "r", bs: "l", label: "into the live turn", num: 6 },
    { a: "f-ans", b: "f-brief", as: "b", bs: "t", num: 7 },
    { a: "f-brief", b: "f-new", as: "r", bs: "l", label: "SAVED", num: 8 },
    { a: "f-new", b: "f-seed", as: "b", bs: "t", num: 9 },
    { a: "f-seed", b: "f-bind", as: "b", bs: "t", label: "only when ready", num: 10 },
    { a: "f-bind", b: "f-say", as: "t", bs: "b", label: "then he sees it", num: 11 },
    { a: "f-say", b: "f-warm", as: "r", bs: "l", num: 12 },
    { a: "f-warm", b: "f-cont", as: "b", bs: "t" },
  ],
  legend: [
    { style: "solid", label: "one message, in order — and it never stops" },
    { style: "dash", label: "no gap left to manage" },
  ],
  padBottom: 86,
}))();

const HEAD: React.CSSProperties = {
  font: "600 15px Inter, system-ui, sans-serif",
  color: "#2F3E4F",
  marginBottom: 12,
  letterSpacing: 0.2,
};

const NOTE: React.CSSProperties = {
  font: "400 13px Inter, system-ui, sans-serif",
  color: "#5B6B7C",
  maxWidth: 940,
  marginTop: 18,
  lineHeight: 1.65,
};

export function FalconSessionRoll() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 46 }}>
      <div>
        <div style={HEAD}>1 &middot; Today &mdash; a guard that blocks its own cure, and a gap after it</div>
        <Canvas d={NOW} mode="contrast" />
      </div>
      <div>
        <div style={HEAD}>2 &middot; Proposed &mdash; delete the guard, seed before you point</div>
        <Canvas d={FIXED} mode="contrast" />
      </div>
      <div style={NOTE}>
        <strong>Why diagram&nbsp;1 deadlocks.</strong> The backstop at step&nbsp;3 runs in the{" "}
        <em>request</em> phase; the roll at steps&nbsp;9&ndash;11 runs in the <em>llm_response</em>{" "}
        phase. A denied request means there is never another llm_response, so the retry counter
        never advances and the give-up-and-roll-anyway path can never execute. Past the hard stop a
        conversation is dead by construction &mdash; no input recovers it. That is what happened to
        Erez&rsquo;s own session on 14&nbsp;Aug at 09:24.
        <br /><br />
        <strong>Why the gap costs either way.</strong> Today the pointer moves before the seed
        lands, so a message in that window reaches a new session with no rules and no history.
        Moving the pointer <em>later</em> instead just trades the failure: the message then reaches
        the old session <em>after</em> its brief was written, so it is answered correctly and
        vanishes from memory. Neither pointer position is safe while the two events are separated.
        <br /><br />
        <strong>Why diagram&nbsp;2 needs no queue.</strong> The roll fires on the round-trip after{" "}
        <code>write_brief</code> and before the agent emits its closing line &mdash; so if seeding
        and binding are synchronous, the entire switch completes <em>inside the turn Dan is still
        waiting on</em>. By the time he can read &ldquo;one moment&rdquo;, there is nothing left to
        wait for. The ordering guarantee is omnigent&rsquo;s own per-session event queue, not
        machinery we build: the seed is enqueued first, so any message can only be processed behind
        it.
        <br /><br />
        Three edits, all subtractive: <strong>delete the request-phase backstop</strong> (~8 lines),{" "}
        <strong>call <code>_seed</code> inline instead of <code>background.add_task</code></strong>,{" "}
        <strong>move <code>_bind_chat</code> after it</strong>. Diagram&nbsp;2 has not shipped.
      </div>
    </div>
  );
}
